import { DSInputText } from '@elliemae/ds-form-input-text';
import { Grid } from '@elliemae/ds-grid';
import { WarningTriangle } from '@elliemae/ds-icons';
import { DSTypography, TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import { memo } from 'react';
import type {
  DialogFieldRow,
  ExceptionFieldErrors,
  ExceptionFieldId,
} from '../types';
import {
  COLUMN_HEADERS,
  NA_DISPLAY,
  OUT_OF_RANGE_MESSAGE,
  PRICE_ADJUSTMENT_MAX,
  PRICE_ADJUSTMENT_MIN,
} from '../utils/constants';
import {
  computeAdjustedValue,
  formatValue,
  parseIntegerInput,
} from '../utils/helpers';
import { StyledInputFlagAnchor, StyledValueFlagAnchor } from './styled';

export interface FieldInputs {
  adjustment: string;
  priceAdjustment: string;
}

interface FieldTableProps {
  rows: DialogFieldRow[];
  inputs: Partial<Record<ExceptionFieldId, FieldInputs>>;
  errors: Partial<Record<ExceptionFieldId, ExceptionFieldErrors>>;
  onAdjustmentChange: (exceptionFieldId: ExceptionFieldId, raw: string) => void;
  onPriceChange: (exceptionFieldId: ExceptionFieldId, raw: string) => void;
}

const COLUMNS = ['1.6fr', '1fr', '1fr', '150px', '1fr', '150px'];

const HeaderCell = ({
  label,
  alignEnd,
}: {
  label: string;
  alignEnd?: boolean;
}) => (
  <Grid justifyContent={alignEnd ? 'flex-end' : 'flex-start'}>
    <DSTypography variant={TYPOGRAPHY_VARIANTS.B4} color="neutral-500">
      {label}
    </DSTypography>
  </Grid>
);

const OutOfRangeIcon = ({ testId }: { testId: string }) => (
  <span className="exception-flag">
    <WarningTriangle
      size="s"
      color={['danger', 900]}
      title={OUT_OF_RANGE_MESSAGE}
      data-testid={testId}
    />
  </span>
);

const ValueCell = ({
  text,
  muted,
  icon,
}: {
  text: string;
  muted?: boolean;
  icon?: JSX.Element;
}) => (
  <StyledValueFlagAnchor>
    {icon}
    <DSTypography
      variant={TYPOGRAPHY_VARIANTS.B2}
      color={muted ? 'neutral-400' : 'neutral-700'}
    >
      {text}
    </DSTypography>
  </StyledValueFlagAnchor>
);

const FieldTableComponent = ({
  rows,
  inputs,
  errors,
  onAdjustmentChange,
  onPriceChange,
}: FieldTableProps) => (
  <Grid data-testid="exception-field-table">
    <Grid
      cols={COLUMNS}
      gutter="m"
      alignItems="center"
      pb="xs"
      borderBottom="1px solid neutral-200"
    >
      <HeaderCell label={COLUMN_HEADERS.field} />
      <HeaderCell label={COLUMN_HEADERS.guideline} alignEnd />
      <HeaderCell label={COLUMN_HEADERS.current} alignEnd />
      <HeaderCell label={COLUMN_HEADERS.adjustment} alignEnd />
      <HeaderCell label={COLUMN_HEADERS.adjusted} alignEnd />
      <HeaderCell label={COLUMN_HEADERS.priceAdjustment} alignEnd />
    </Grid>

    {rows.map((row) => {
      const { exceptionFieldId, fieldName, format, isDisabled } = row;
      const rowErrors = errors[exceptionFieldId];
      const fieldInputs = inputs[exceptionFieldId] ?? {
        adjustment: '',
        priceAdjustment: '',
      };
      const adjustment = parseIntegerInput(fieldInputs.adjustment);
      const hasAdjustment = adjustment !== null && adjustment !== 0;
      const adjustedValue =
        isDisabled || !hasAdjustment
          ? row.currentValue
          : computeAdjustedValue(row.currentValue, adjustment);

      return (
        <Grid
          key={exceptionFieldId}
          cols={COLUMNS}
          gutter="m"
          alignItems="center"
          py="xxs"
          borderBottom="1px solid neutral-080"
        >
          <DSTypography variant={TYPOGRAPHY_VARIANTS.B2} color="neutral-700">
            {fieldName}
          </DSTypography>

          {isDisabled ? (
            <>
              <ValueCell text={NA_DISPLAY} muted />
              <ValueCell text="" />
            </>
          ) : (
            <>
              <ValueCell text={formatValue(row.guidelineValue, format)} />
              <ValueCell text={formatValue(row.currentValue, format)} />
            </>
          )}

          <Grid justifyContent="flex-end">
            <DSInputText
              id={`exception-adjustment-${exceptionFieldId}`}
              type="number"
              aria-label={`${fieldName} adjustment`}
              disabled={isDisabled}
              value={fieldInputs.adjustment}
              onValueChange={(value) =>
                onAdjustmentChange(exceptionFieldId, String(value ?? ''))
              }
            />
          </Grid>

          <ValueCell
            text={isDisabled ? NA_DISPLAY : formatValue(adjustedValue, format)}
            muted={isDisabled}
            icon={
              rowErrors?.adjustedValue ? (
                <OutOfRangeIcon
                  testId={`exception-adjusted-error-${exceptionFieldId}`}
                />
              ) : undefined
            }
          />

          <Grid justifyContent="flex-end">
            <StyledInputFlagAnchor>
              {rowErrors?.priceAdjustment && (
                <OutOfRangeIcon
                  testId={`exception-price-error-${exceptionFieldId}`}
                />
              )}
              <DSInputText
                id={`exception-price-adjustment-${exceptionFieldId}`}
                type="number"
                aria-label={`${fieldName} price adjustment`}
                disabled={isDisabled || !hasAdjustment}
                min={PRICE_ADJUSTMENT_MIN}
                max={PRICE_ADJUSTMENT_MAX}
                value={fieldInputs.priceAdjustment}
                onValueChange={(value) =>
                  onPriceChange(exceptionFieldId, String(value ?? ''))
                }
              />
            </StyledInputFlagAnchor>
          </Grid>
        </Grid>
      );
    })}
  </Grid>
);

export const FieldTable = memo(FieldTableComponent);
